<script setup>
import { onMounted, computed, ref, toRefs } from 'vue';
import { useTimeoutFn } from '@vueuse/core';
import { provideMessageContext } from './provider.js';
import { useAlert, useTrack } from 'dashboard/composables';
import { useMapGetter } from 'dashboard/composables/store';
import { emitter } from 'shared/helpers/mitt';
import { useI18n } from 'vue-i18n';
import { useRoute, useRouter } from 'vue-router';
import { useStore } from 'vuex';
import { LocalStorage } from 'shared/helpers/localStorage';
import { ACCOUNT_EVENTS } from 'dashboard/helper/AnalyticsHelper/events';
import { LOCAL_STORAGE_KEYS } from 'dashboard/constants/localStorage';
import { getInboxIconByType } from 'dashboard/helper/inbox';
import { BUS_EVENTS } from 'shared/constants/busEvents';
import ConversationAPI from 'dashboard/api/conversations';
import { frontendURL, conversationUrl } from 'dashboard/helper/URLHelper';
import {
  MESSAGE_TYPES,
  ATTACHMENT_TYPES,
  MESSAGE_VARIANTS,
  SENDER_TYPES,
  ORIENTATION,
  MESSAGE_STATUS,
  CONTENT_TYPES,
} from './constants';

import Avatar from 'next/avatar/Avatar.vue';

import TextBubble from './bubbles/Text/Index.vue';
import ActivityBubble from './bubbles/Activity.vue';
import ImageBubble from './bubbles/Image.vue';
import StickerBubble from './bubbles/Sticker.vue';
import FileBubble from './bubbles/File.vue';
import AudioBubble from './bubbles/Audio.vue';
import VideoBubble from './bubbles/Video.vue';
import EmbedBubble from './bubbles/Embed.vue';
import FallbackBubble from './bubbles/Fallback.vue';
import InstagramStoryBubble from './bubbles/InstagramStory.vue';
import EmailBubble from './bubbles/Email/Index.vue';
import UnsupportedBubble from './bubbles/Unsupported.vue';
import ContactBubble from './bubbles/Contact.vue';
import DyteBubble from './bubbles/Dyte.vue';
import LocationBubble from './bubbles/Location.vue';
import CSATBubble from './bubbles/CSAT.vue';
import FormBubble from './bubbles/Form.vue';
import VoiceCallBubble from './bubbles/VoiceCall.vue';

import MessageError from './MessageError.vue';
import ContextMenu from 'dashboard/modules/conversations/components/MessageContextMenu.vue';
import { useBranding } from 'shared/composables/useBranding';
import { useAdmin } from 'dashboard/composables/useAdmin';

/**
 * @typedef {Object} Attachment
 * @property {number} id - Unique identifier for the attachment
 * @property {number} messageId - ID of the associated message
 * @property {'image'|'audio'|'video'|'file'|'location'|'fallback'|'share'|'story_mention'|'contact'|'ig_reel'} fileType - Type of the attachment (file or image)
 * @property {number} accountId - ID of the associated account
 * @property {string|null} extension - File extension
 * @property {string} dataUrl - URL to access the full attachment data
 * @property {string} thumbUrl - URL to access the thumbnail version
 * @property {number} fileSize - Size of the file in bytes
 * @property {number|null} width - Width of the image if applicable
 * @property {number|null} height - Height of the image if applicable
 */

/**
 * @typedef {Object} Sender
 * @property {Object} additional_attributes - Additional attributes of the sender
 * @property {Object} custom_attributes - Custom attributes of the sender
 * @property {string} email - Email of the sender
 * @property {number} id - ID of the sender
 * @property {string|null} identifier - Identifier of the sender
 * @property {string} name - Name of the sender
 * @property {string|null} phone_number - Phone number of the sender
 * @property {string} thumbnail - Thumbnail URL of the sender
 * @property {string} type - Type of sender
 */

/**
 * @typedef {Object} ContentAttributes
 * @property {string} externalError - an error message to be shown if the message failed to send
 */

/**
 * @typedef {Object} Props
 * @property {('sent'|'delivered'|'read'|'failed'|'progress')} status - The delivery status of the message
 * @property {ContentAttributes} [contentAttributes={}] - Additional attributes of the message content
 * @property {Attachment[]} [attachments=[]] - The attachments associated with the message
 * @property {Sender|null} [sender=null] - The sender information
 * @property {boolean} [private=false] - Whether the message is private
 * @property {number|null} [senderId=null] - The ID of the sender
 * @property {number} createdAt - Timestamp when the message was created
 * @property {number} currentUserId - The ID of the current user
 * @property {number} id - The unique identifier for the message
 * @property {number} messageType - The type of message (must be one of MESSAGE_TYPES)
 * @property {string|null} [error=null] - Error message if the message failed to send
 * @property {string|null} [senderType=null] - The type of the sender
 * @property {string} content - The message content
 * @property {boolean} [groupWithNext=false] - Whether the message should be grouped with the next message
 * @property {Object|null} [inReplyTo=null] - The message to which this message is a reply
 * @property {boolean} [isEmailInbox=false] - Whether the message is from an email inbox
 * @property {number} conversationId - The ID of the conversation to which the message belongs
 * @property {number} inboxId - The ID of the inbox to which the message belongs
 */

// eslint-disable-next-line vue/define-macros-order
const props = defineProps({
  id: { type: Number, required: true },
  messageType: {
    type: Number,
    required: true,
    validator: value => Object.values(MESSAGE_TYPES).includes(value),
  },
  status: {
    type: String,
    required: true,
    validator: value => Object.values(MESSAGE_STATUS).includes(value),
  },
  attachments: { type: Array, default: () => [] },
  call: { type: Object, default: null }, // eslint-disable-line vue/no-unused-properties
  content: { type: String, default: null },
  contentAttributes: { type: Object, default: () => ({}) },
  contentType: {
    type: String,
    default: 'text',
    validator: value => Object.values(CONTENT_TYPES).includes(value),
  },
  conversationId: { type: Number, required: true },
  createdAt: { type: Number, required: true }, // eslint-disable-line vue/no-unused-properties
  currentUserId: { type: Number, required: true }, // eslint-disable-line vue/no-unused-properties
  groupWithNext: { type: Boolean, default: false },
  inboxId: { type: Number, default: null }, // eslint-disable-line vue/no-unused-properties
  inboxSupportsReplyTo: { type: Object, default: () => ({}) },
  inReplyTo: { type: Object, default: null }, // eslint-disable-line vue/no-unused-properties
  isEmailInbox: { type: Boolean, default: false },
  private: { type: Boolean, default: false },
  additionalAttributes: { type: Object, default: () => ({}) }, // eslint-disable-line vue/no-unused-properties
  sender: { type: Object, default: null },
  senderId: { type: Number, default: null },
  senderType: { type: String, default: null },
  sourceId: { type: String, default: '' }, // eslint-disable-line vue/no-unused-properties
});

const emit = defineEmits(['retry']);

const contextMenuPosition = ref({});
const showBackgroundHighlight = ref(false);
const showContextMenu = ref(false);
const { t } = useI18n();
const route = useRoute();
const router = useRouter();
const store = useStore();
const inboxGetter = useMapGetter('inboxes/getInbox');
const selectedChat = useMapGetter('getSelectedChat');
const { isAdmin } = useAdmin();
const inbox = computed(() => inboxGetter.value(props.inboxId) || {});
const accountId = computed(() => store.getters.getCurrentAccountId);
const isStartingWhatsappConversation = ref(false);
const { replaceInstallationName } = useBranding();

const isInternalTeamChat = computed(
  () => !!selectedChat.value?.additional_attributes?.internal_team_chat
);
const isInternalTeamRoom = computed(
  () =>
    isInternalTeamChat.value &&
    !selectedChat.value?.additional_attributes?.internal_team_private_chat
);
const isWhatsappGroup = computed(() =>
  selectedChat.value?.meta?.sender?.identifier?.endsWith('@g.us')
);
const isEvolutionWhatsapp = computed(
  () =>
    inbox.value?.channel_type === 'Channel::Api' && !isInternalTeamChat.value
);
const whatsappGroupSender = computed(() => {
  if (!isWhatsappGroup.value || props.messageType !== MESSAGE_TYPES.INCOMING) {
    return '';
  }

  return props.content?.match(/^\*\*(.+?):\*\*(?:\r?\n){0,2}/)?.[1] || '';
});
const displayContent = computed(() => {
  if (!whatsappGroupSender.value) return props.content;

  return props.content.replace(/^\*\*(.+?):\*\*(?:\r?\n){0,2}/, '');
});
const isOwnInternalMessage = computed(
  () =>
    Number(props.senderId ?? props.sender?.id) === Number(props.currentUserId)
);

/**
 * Computes the message variant based on props
 * @type {import('vue').ComputedRef<'user'|'agent'|'activity'|'private'|'bot'|'template'>}
 */
const variant = computed(() => {
  if (props.private) return MESSAGE_VARIANTS.PRIVATE;

  if (isInternalTeamChat.value) {
    return isOwnInternalMessage.value
      ? MESSAGE_VARIANTS.AGENT
      : MESSAGE_VARIANTS.USER;
  }

  if (props.isEmailInbox) {
    const emailInboxTypes = [MESSAGE_TYPES.INCOMING, MESSAGE_TYPES.OUTGOING];
    if (emailInboxTypes.includes(props.messageType)) {
      return MESSAGE_VARIANTS.EMAIL;
    }
  }

  if (props.contentType === CONTENT_TYPES.INCOMING_EMAIL) {
    return MESSAGE_VARIANTS.EMAIL;
  }

  if (props.status === MESSAGE_STATUS.FAILED) return MESSAGE_VARIANTS.ERROR;
  if (props.contentAttributes?.isUnsupported)
    return MESSAGE_VARIANTS.UNSUPPORTED;

  if (props.contentAttributes?.externalEcho) {
    return MESSAGE_VARIANTS.AGENT;
  }

  const isBot =
    props.sender?.type === SENDER_TYPES.AGENT_BOT ||
    props.senderType === SENDER_TYPES.AGENT_BOT ||
    (!props.sender && !props.additionalAttributes?.senderName);
  if (isBot && props.messageType === MESSAGE_TYPES.OUTGOING) {
    return MESSAGE_VARIANTS.BOT;
  }

  const variants = {
    [MESSAGE_TYPES.INCOMING]: MESSAGE_VARIANTS.USER,
    [MESSAGE_TYPES.ACTIVITY]: MESSAGE_VARIANTS.ACTIVITY,
    [MESSAGE_TYPES.OUTGOING]: MESSAGE_VARIANTS.AGENT,
    [MESSAGE_TYPES.TEMPLATE]: MESSAGE_VARIANTS.TEMPLATE,
  };

  return variants[props.messageType] || MESSAGE_VARIANTS.USER;
});

const isBotOrAgentMessage = computed(() => {
  if (props.messageType === MESSAGE_TYPES.ACTIVITY) {
    return false;
  }
  // if an outgoing message is still processing, then it's definitely a
  // message sent by the current user
  if (
    props.status === MESSAGE_STATUS.PROGRESS &&
    props.messageType === MESSAGE_TYPES.OUTGOING
  ) {
    return true;
  }
  const senderId = props.senderId ?? props.sender?.id;
  const senderType = props.sender?.type ?? props.senderType;

  if (!senderType || !senderId) {
    return true;
  }

  if (
    [SENDER_TYPES.AGENT_BOT, SENDER_TYPES.CAPTAIN_ASSISTANT].includes(
      senderType
    )
  ) {
    return true;
  }

  return senderType.toLowerCase() === SENDER_TYPES.USER.toLowerCase();
});

/**
 * Computes the message orientation based on sender type and message type
 * @returns {import('vue').ComputedRef<'left'|'right'|'center'>} The computed orientation
 */
const orientation = computed(() => {
  if (isInternalTeamChat.value) {
    return isOwnInternalMessage.value ? ORIENTATION.RIGHT : ORIENTATION.LEFT;
  }

  if (isBotOrAgentMessage.value) {
    return ORIENTATION.RIGHT;
  }

  if (props.messageType === MESSAGE_TYPES.ACTIVITY) return ORIENTATION.CENTER;

  return ORIENTATION.LEFT;
});

const flexOrientationClass = computed(() => {
  const map = {
    [ORIENTATION.LEFT]: 'justify-start',
    [ORIENTATION.RIGHT]: 'justify-end',
    [ORIENTATION.CENTER]: 'justify-center',
  };

  return map[orientation.value];
});

const gridClass = computed(() => {
  const map = {
    [ORIENTATION.LEFT]: 'grid grid-cols-1fr',
    [ORIENTATION.RIGHT]: 'grid grid-cols-[1fr_24px]',
  };

  return map[orientation.value];
});

const gridTemplate = computed(() => {
  const map = {
    [ORIENTATION.LEFT]: `
      "bubble"
      "meta"
    `,
    [ORIENTATION.RIGHT]: `
      "bubble avatar"
      "meta spacer"
    `,
  };

  return map[orientation.value];
});

const shouldGroupWithNext = computed(() => {
  if (props.status === MESSAGE_STATUS.FAILED) return false;

  return props.groupWithNext;
});

const shouldShowAvatar = computed(() => {
  if (props.messageType === MESSAGE_TYPES.ACTIVITY) return false;
  if (orientation.value === ORIENTATION.LEFT) return false;

  return true;
});

const componentToRender = computed(() => {
  if (props.isEmailInbox && !props.private) {
    const emailInboxTypes = [MESSAGE_TYPES.INCOMING, MESSAGE_TYPES.OUTGOING];
    if (emailInboxTypes.includes(props.messageType)) return EmailBubble;
  }

  if (props.contentType === CONTENT_TYPES.INPUT_CSAT) {
    return CSATBubble;
  }

  if (
    [CONTENT_TYPES.INPUT_SELECT, CONTENT_TYPES.FORM].includes(props.contentType)
  ) {
    return FormBubble;
  }

  if (props.contentType === CONTENT_TYPES.VOICE_CALL) {
    return VoiceCallBubble;
  }

  if (props.contentType === CONTENT_TYPES.INCOMING_EMAIL) {
    return EmailBubble;
  }

  if (props.contentAttributes?.isUnsupported) {
    return UnsupportedBubble;
  }

  if (props.contentAttributes.type === 'dyte') {
    return DyteBubble;
  }

  if (
    props.contentType === CONTENT_TYPES.STICKER &&
    Array.isArray(props.attachments) &&
    props.attachments.length
  ) {
    return StickerBubble;
  }

  const instagramSharedTypes = [
    ATTACHMENT_TYPES.STORY_MENTION,
    ATTACHMENT_TYPES.IG_STORY,
    ATTACHMENT_TYPES.IG_STORY_REPLY,
    ATTACHMENT_TYPES.IG_POST,
  ];
  if (instagramSharedTypes.includes(props.contentAttributes.imageType)) {
    return InstagramStoryBubble;
  }

  if (Array.isArray(props.attachments) && props.attachments.length === 1) {
    const fileType = props.attachments[0].fileType;

    if (fileType === ATTACHMENT_TYPES.FALLBACK) return FallbackBubble;

    // Keep image messages in the media bubble even when they include a
    // caption. Rendering a captioned image in TextBubble turns the image into
    // a small attachment chip instead of the full-size media preview.
    if (fileType === ATTACHMENT_TYPES.IMAGE) return ImageBubble;

    if (!props.content) {
      if (fileType === ATTACHMENT_TYPES.FILE) return FileBubble;
      if (fileType === ATTACHMENT_TYPES.AUDIO) return AudioBubble;
      if (fileType === ATTACHMENT_TYPES.VIDEO) return VideoBubble;
      if (fileType === ATTACHMENT_TYPES.IG_REEL) return VideoBubble;
      if (fileType === ATTACHMENT_TYPES.EMBED) return EmbedBubble;
      if (fileType === ATTACHMENT_TYPES.LOCATION) return LocationBubble;
    }
    // Attachment content is the name of the contact
    if (fileType === ATTACHMENT_TYPES.CONTACT) return ContactBubble;
  }

  return TextBubble;
});

const shouldShowContextMenu = computed(() => {
  return !props.contentAttributes?.isUnsupported;
});

const isBubble = computed(() => {
  return props.messageType !== MESSAGE_TYPES.ACTIVITY;
});

const isMessageDeleted = computed(() => {
  return (
    props.contentAttributes?.deleted || props.contentAttributes?.deletedForMe
  );
});
const isDeletedForEveryone = computed(
  () => props.contentAttributes?.deleted === true
);
const isAdminDeletedView = computed(
  () =>
    isAdmin.value &&
    isDeletedForEveryone.value &&
    props.contentAttributes?.adminDeletedView === true
);

const showEditHistory = ref(false);
const editHistory = computed(
  () =>
    props.contentAttributes?.editHistory ||
    props.contentAttributes?.edit_history ||
    []
);
const wasEdited = computed(
  () =>
    !isMessageDeleted.value &&
    (props.contentAttributes?.edited || editHistory.value.length > 0)
);
const formatEditDate = value => {
  if (!value) return '';

  return new Intl.DateTimeFormat(undefined, {
    dateStyle: 'short',
    timeStyle: 'short',
  }).format(new Date(value));
};

const payloadForContextMenu = computed(() => {
  return {
    id: props.id,
    content_attributes: props.contentAttributes,
    content: props.content,
    conversation_id: props.conversationId,
    source_id: props.sourceId,
    inbox_id: props.inboxId,
  };
});

const contextMenuEnabledOptions = computed(() => {
  const hasText = !!props.content;
  const hasAttachments = !!(props.attachments && props.attachments.length > 0);

  const isOutgoing = props.messageType === MESSAGE_TYPES.OUTGOING;
  const isOwnOutgoing =
    isOutgoing && (isOwnInternalMessage.value || isAdmin.value);
  const isFailedOrProcessing =
    props.status === MESSAGE_STATUS.FAILED ||
    props.status === MESSAGE_STATUS.PROGRESS;

  return {
    copy: hasText,
    delete:
      (hasText || hasAttachments) &&
      !isFailedOrProcessing &&
      !isMessageDeleted.value &&
      !(isEvolutionWhatsapp.value && isOwnOutgoing),
    editWhatsapp:
      isEvolutionWhatsapp.value &&
      isOwnOutgoing &&
      hasText &&
      !hasAttachments &&
      !!props.sourceId &&
      !isFailedOrProcessing &&
      !isMessageDeleted.value &&
      Date.now() / 1000 - props.createdAt <= 15 * 60,
    reactWhatsapp:
      isEvolutionWhatsapp.value &&
      !!props.sourceId &&
      !props.private &&
      !isFailedOrProcessing &&
      !isMessageDeleted.value,
    forwardWhatsapp:
      isEvolutionWhatsapp.value &&
      !!props.sourceId &&
      !props.private &&
      !isFailedOrProcessing &&
      !isMessageDeleted.value,
    deleteWhatsapp:
      isEvolutionWhatsapp.value &&
      isOwnOutgoing &&
      !!props.sourceId &&
      !isFailedOrProcessing &&
      !isMessageDeleted.value,
    deleteWhatsappForMe:
      isAdmin.value &&
      isEvolutionWhatsapp.value &&
      isOwnOutgoing &&
      !!props.sourceId &&
      !isFailedOrProcessing &&
      !isMessageDeleted.value,
    cannedResponse: isOutgoing && hasText && !isMessageDeleted.value,
    copyLink: !isFailedOrProcessing,
    translate: !isFailedOrProcessing && !isMessageDeleted.value && hasText,
    replyTo:
      !props.private &&
      props.inboxSupportsReplyTo.outgoing &&
      !isFailedOrProcessing,
  };
});

const whatsappReactions = computed(() => {
  const reactions = props.contentAttributes?.whatsappReactions || [];
  return Object.values(
    reactions.reduce((groups, reaction) => {
      if (!reaction.emoji) return groups;
      const group = groups[reaction.emoji] || {
        emoji: reaction.emoji,
        count: 0,
        names: [],
        reactedByMe: false,
      };
      group.count += 1;
      if (reaction.actorName) group.names.push(reaction.actorName);
      group.reactedByMe ||= reaction.fromMe;
      groups[reaction.emoji] = group;
      return groups;
    }, {})
  );
});

const shouldRenderMessage = computed(() => {
  const hasAttachments = !!(props.attachments && props.attachments.length > 0);
  const isEmailContentType = props.contentType === CONTENT_TYPES.INCOMING_EMAIL;
  const isUnsupported = props.contentAttributes?.isUnsupported;
  const isAnIntegrationMessage =
    props.contentType === CONTENT_TYPES.INTEGRATIONS;
  const isFailedMessage = props.status === MESSAGE_STATUS.FAILED;
  const hasExternalError = !!props.contentAttributes?.externalError;

  return (
    hasAttachments ||
    props.content ||
    isEmailContentType ||
    isUnsupported ||
    isAnIntegrationMessage ||
    isFailedMessage ||
    hasExternalError
  );
});

function openContextMenu(e) {
  const shouldSkipContextMenu =
    e.target?.classList.contains('skip-context-menu') ||
    ['a', 'img'].includes(e.target?.tagName.toLowerCase());
  if (shouldSkipContextMenu || getSelection().toString()) {
    return;
  }

  e.preventDefault();
  if (e.type === 'contextmenu') {
    useTrack(ACCOUNT_EVENTS.OPEN_MESSAGE_CONTEXT_MENU);
  }
  contextMenuPosition.value = {
    x: e.pageX || e.clientX,
    y: e.pageY || e.clientY,
  };
  showContextMenu.value = true;
}

function closeContextMenu() {
  showContextMenu.value = false;
  contextMenuPosition.value = { x: null, y: null };
}

function handleReplyTo() {
  const replyStorageKey = LOCAL_STORAGE_KEYS.MESSAGE_REPLY_TO;
  const { conversationId, id: replyTo } = props;

  LocalStorage.updateJsonStore(replyStorageKey, conversationId, replyTo);
  emitter.emit(BUS_EVENTS.TOGGLE_REPLY_TO_MESSAGE, props);
}

const avatarInfo = computed(() => {
  if (props.contentAttributes?.externalEcho) {
    const { name, avatar_url, channel_type, medium } = inbox.value;
    const iconName = avatar_url
      ? null
      : getInboxIconByType(channel_type, medium);
    return {
      name: iconName ? '' : name || t('CONVERSATION.NATIVE_APP'),
      src: avatar_url || '',
      iconName,
    };
  }

  // If no sender, check for Slack (or other integration) sender info
  if (!props.sender) {
    const { senderName, senderAvatarUrl } = props.additionalAttributes || {};
    if (senderName) {
      return { name: senderName, src: senderAvatarUrl ?? '' };
    }
    return { name: t('CONVERSATION.BOT'), src: '' };
  }

  const { sender } = props;
  const { name, type, avatarUrl, thumbnail } = sender || {};

  // If sender type is agent bot, use avatarUrl
  if ([SENDER_TYPES.AGENT_BOT, SENDER_TYPES.CAPTAIN_ASSISTANT].includes(type)) {
    return {
      name: name ?? '',
      src: avatarUrl ?? '',
    };
  }

  // For all other senders, use thumbnail
  return {
    name: name ?? '',
    src: thumbnail ?? '',
  };
});

const avatarTooltip = computed(() => {
  if (props.contentAttributes?.externalEcho) {
    return replaceInstallationName(t('CONVERSATION.NATIVE_APP_ADVISORY'));
  }
  if (avatarInfo.value.name === '') return '';
  return `${t('CONVERSATION.SENT_BY')} ${avatarInfo.value.name}`;
});

const shouldShowInternalSenderName = computed(
  () =>
    isInternalTeamRoom.value &&
    !shouldGroupWithNext.value &&
    !!avatarInfo.value.name
);
const shouldShowWhatsappGroupSender = computed(
  () => !!whatsappGroupSender.value && !shouldGroupWithNext.value
);

const normalizePhone = value => String(value || '').replace(/\D/g, '');

const findGroupParticipant = participants => {
  const senderLabel = whatsappGroupSender.value.trim().toLocaleLowerCase();
  const senderPhone = normalizePhone(senderLabel);
  return participants.find(participant => {
    const names = [
      participant.saved_name,
      participant.savedName,
      participant.display_name,
      participant.displayName,
      participant.phone_number,
      participant.phoneNumber,
      participant.jid,
      participant.id,
    ];
    return names.some(value => {
      const normalizedValue = String(value || '')
        .trim()
        .toLocaleLowerCase();
      return (
        normalizedValue === senderLabel ||
        (senderPhone && normalizePhone(normalizedValue) === senderPhone)
      );
    });
  });
};

const whatsappGroupSenderDisplayName = computed(() => {
  const senderPhone = normalizePhone(whatsappGroupSender.value);
  if (!senderPhone) return whatsappGroupSender.value;

  const participants =
    selectedChat.value?.additional_attributes?.whatsapp_group_participants ||
    [];
  return (
    findGroupParticipant(participants)?.saved_name || whatsappGroupSender.value
  );
});

const openWhatsappGroupSenderConversation = async () => {
  if (isStartingWhatsappConversation.value) return;

  isStartingWhatsappConversation.value = true;
  try {
    let participants =
      selectedChat.value?.additional_attributes?.whatsapp_group_participants ||
      [];
    if (!participants.length) {
      const { data } = await ConversationAPI.getWhatsappGroupParticipants(
        selectedChat.value.id
      );
      participants = data.participants || [];
    }

    const participant = findGroupParticipant(participants);
    const phoneNumber = normalizePhone(
      participant?.phone_number ||
        participant?.phoneNumber ||
        whatsappGroupSender.value
    );
    if (!phoneNumber) throw new Error('Participant phone number not found');

    const { data } = await ConversationAPI.startWhatsappConversation(
      selectedChat.value.id,
      phoneNumber
    );
    if (!data.conversation_id) {
      if (data.contact_already_saved) {
        useAlert(t('INTERNAL_CHAT.GROUP_CONTACT_ALREADY_EXISTS'));
      }
      await router.push({
        name: 'contacts_edit',
        params: { accountId: accountId.value, contactId: data.contact_id },
        query: {
          compose: 'true',
          inbox_id: data.inbox_id,
          return_to_conversation: frontendURL(
            conversationUrl({
              accountId: accountId.value,
              activeInbox: selectedChat.value.inbox_id,
              id: selectedChat.value.id,
            })
          ),
        },
      });
      return;
    }
    await store.dispatch('getConversation', data.conversation_id);
    await router.push({
      path: frontendURL(
        conversationUrl({
          accountId: accountId.value,
          activeInbox: selectedChat.value.inbox_id,
          id: data.conversation_id,
        })
      ),
    });
  } catch (error) {
    useAlert(t('INTERNAL_CHAT.GROUP_START_ERROR'));
  } finally {
    isStartingWhatsappConversation.value = false;
  }
};

const setupHighlightTimer = () => {
  if (Number(route.query.messageId) !== Number(props.id)) {
    return;
  }

  showBackgroundHighlight.value = true;
  const HIGHLIGHT_TIMER = 1000;
  useTimeoutFn(() => {
    showBackgroundHighlight.value = false;
  }, HIGHLIGHT_TIMER);
};

onMounted(setupHighlightTimer);

provideMessageContext({
  ...toRefs(props),
  content: displayContent,
  isPrivate: computed(() => props.private),
  variant,
  orientation,
  isBotOrAgentMessage,
  shouldGroupWithNext,
});
</script>

<!-- eslint-disable-next-line vue/no-root-v-if -->
<template>
  <div
    v-if="shouldRenderMessage"
    :id="`message${props.id}`"
    class="flex w-full mb-2 message-bubble-container"
    :data-message-id="props.id"
    :class="[
      flexOrientationClass,
      {
        'group-with-next': shouldGroupWithNext,
        'bg-n-alpha-1': showBackgroundHighlight,
      },
    ]"
  >
    <div v-if="variant === MESSAGE_VARIANTS.ACTIVITY">
      <ActivityBubble :content="content" />
    </div>
    <div
      v-else
      :class="[
        gridClass,
        {
          'gap-y-2': contentAttributes.externalError,
          'w-full': variant === MESSAGE_VARIANTS.EMAIL,
        },
      ]"
      class="gap-x-2"
      :style="{
        gridTemplateAreas: gridTemplate,
      }"
    >
      <div
        v-if="!shouldGroupWithNext && shouldShowAvatar"
        v-tooltip.left-end="avatarTooltip"
        class="[grid-area:avatar] flex items-end"
      >
        <Avatar v-bind="avatarInfo" :size="24" />
      </div>
      <div
        class="[grid-area:bubble] flex min-w-0"
        :class="{
          'ltr:ml-8 rtl:mr-8 justify-end': orientation === ORIENTATION.RIGHT,
          'ltr:mr-8 rtl:ml-8': orientation === ORIENTATION.LEFT,
          'deleted-message-admin': isAdminDeletedView,
        }"
        @contextmenu="openContextMenu($event)"
      >
        <div
          class="flex min-w-0 flex-col"
          :class="
            orientation === ORIENTATION.RIGHT ? 'items-end' : 'items-start'
          "
        >
          <span
            v-if="shouldShowInternalSenderName"
            class="mb-1 px-1 text-xs font-medium text-n-slate-11"
          >
            {{ avatarInfo.name }}
          </span>
          <button
            v-if="shouldShowWhatsappGroupSender"
            v-tooltip.top="$t('INTERNAL_CHAT.GROUP_START')"
            type="button"
            class="mb-1 px-1 text-xs font-medium text-n-brand hover:underline disabled:opacity-60"
            :disabled="isStartingWhatsappConversation"
            @click.stop="openWhatsappGroupSenderConversation"
          >
            {{ whatsappGroupSenderDisplayName }}
          </button>
          <span
            v-if="isAdminDeletedView"
            class="mb-1 px-1 text-xs font-medium text-n-ruby-11"
          >
            {{ $t('CONVERSATION.DELETED_MESSAGE.ADMIN_NOTICE') }}
          </span>
          <Component :is="componentToRender" />
          <div
            v-if="whatsappReactions.length"
            class="mt-1 flex max-w-full flex-wrap gap-1 px-1"
          >
            <span
              v-for="reaction in whatsappReactions"
              :key="reaction.emoji"
              v-tooltip.top="
                reaction.names.length ? reaction.names.join(', ') : undefined
              "
              class="inline-flex min-h-6 items-center gap-1 rounded-full border px-2 py-0.5 text-xs"
              :class="
                reaction.reactedByMe
                  ? 'border-n-brand bg-n-brand/10 text-n-brand'
                  : 'border-n-strong bg-n-alpha-1 text-n-slate-12'
              "
            >
              <span class="text-sm">{{ reaction.emoji }}</span>
              <span v-if="reaction.count > 1">{{ reaction.count }}</span>
            </span>
          </div>
          <div
            v-if="wasEdited"
            class="mt-1 max-w-full px-1 text-xs text-n-slate-10"
            :class="
              orientation === ORIENTATION.RIGHT ? 'text-right' : 'text-left'
            "
          >
            <button
              type="button"
              class="hover:text-n-slate-12 hover:underline"
              @click.stop="showEditHistory = !showEditHistory"
            >
              {{ $t('CONVERSATION.EDIT_HISTORY.EDITED') }}
              <template v-if="editHistory.length">
                {{ $t('CONVERSATION.EDIT_HISTORY.SHOW') }}
              </template>
            </button>
            <div
              v-if="showEditHistory && editHistory.length"
              class="mt-1 flex max-w-96 flex-col gap-2 rounded-lg border border-n-strong bg-n-solid-1 p-3 text-left shadow-sm"
            >
              <div
                v-for="(entry, index) in editHistory"
                :key="`${entry.editedAt || entry.edited_at}-${index}`"
                class="border-b border-n-weak pb-2 last:border-0 last:pb-0"
              >
                <p
                  class="m-0 whitespace-pre-wrap break-words text-sm text-n-slate-12"
                >
                  {{ entry.content }}
                </p>
                <span class="mt-1 block text-xs text-n-slate-9">
                  {{ formatEditDate(entry.editedAt || entry.edited_at) }}
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>
      <MessageError
        v-if="contentAttributes.externalError"
        class="[grid-area:meta]"
        :class="flexOrientationClass"
        :error="contentAttributes.externalError"
        @retry="emit('retry')"
      />
    </div>
    <div v-if="shouldShowContextMenu" class="context-menu-wrap">
      <ContextMenu
        v-if="isBubble"
        :context-menu-position="contextMenuPosition"
        :is-open="showContextMenu"
        :enabled-options="contextMenuEnabledOptions"
        :message="payloadForContextMenu"
        hide-button
        @open="openContextMenu"
        @close="closeContextMenu"
        @reply-to="handleReplyTo"
      />
    </div>
  </div>
</template>

<style lang="scss">
.group-with-next + .message-bubble-container {
  .left-bubble {
    @apply ltr:rounded-tl-sm rtl:rounded-tr-sm;
  }

  .right-bubble {
    @apply ltr:rounded-tr-sm rtl:rounded-tl-sm;
  }
}

.deleted-message-admin {
  .left-bubble,
  .right-bubble {
    @apply bg-n-ruby-3 ring-1 ring-n-ruby-5;
  }
}
</style>
